import { useCallback, useEffect, useRef, useState } from 'react'

import { getNewUuid } from '@avantstay/backoffice-core'

// Mirrors the Remy backend attachment policy (REMY_ATTACHMENT_MAX_FILES /
// REMY_ATTACHMENT_MAX_FILE_MB defaults) — files over these caps would be
// declined server-side anyway, so reject them at staging time instead.
export const MAX_ATTACHMENTS = 5
export const MAX_ATTACHMENT_SIZE_MB = 10
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024

export interface ChatAttachment {
  id: string
  file: File
  // Object URL backing the chip thumbnail. Created only for images; revoked on
  // remove/clear/unmount so blobs don't leak.
  previewUrl?: string
}

const dedupeKey = (file: File) => `${file.name}|${file.size}|${file.lastModified}`

// Message attachments staged in the chat input: any file type, capped at
// MAX_ATTACHMENTS / MAX_ATTACHMENT_SIZE_MB. Over-limit files are rejected with
// an inline error while the rest of the batch is still accepted; duplicates
// (same name+size+lastModified) are skipped silently.
export function useFileAttachments() {
  const [attachments, setAttachments] = useState<ChatAttachment[]>([])
  const [error, setError] = useState<string | null>(null)

  // Mutations compute from this ref (not a setState updater) so object URLs are
  // created exactly once even under StrictMode's double-invoked updaters.
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments

  // Unmount-only cleanup for whatever is still attached.
  useEffect(
    () => () => {
      attachmentsRef.current.forEach(a => a.previewUrl && URL.revokeObjectURL(a.previewUrl))
    },
    [],
  )

  const addFiles = useCallback((files: FileList | File[]) => {
    const current = attachmentsRef.current

    const seen = new Set(current.map(a => dedupeKey(a.file)))
    const incoming: File[] = []
    for (const file of Array.from(files)) {
      const key = dedupeKey(file)
      if (seen.has(key)) continue
      seen.add(key)
      incoming.push(file)
    }

    const oversize = incoming.filter(f => f.size > MAX_ATTACHMENT_SIZE_BYTES)
    const fitting = incoming.filter(f => f.size <= MAX_ATTACHMENT_SIZE_BYTES)
    const slots = Math.max(0, MAX_ATTACHMENTS - current.length)
    const accepted = fitting.slice(0, slots)

    const problems: string[] = []
    if (oversize.length > 0) {
      const names = oversize.map(f => f.name).join(', ')
      const verb = oversize.length === 1 ? 'is' : 'are'
      problems.push(`${names} ${verb} larger than ${MAX_ATTACHMENT_SIZE_MB} MB`)
    }
    if (fitting.length > accepted.length) {
      problems.push(`you can attach up to ${MAX_ATTACHMENTS} files`)
    }
    setError(problems.length > 0 ? `Some files were not attached: ${problems.join('; ')}.` : null)

    if (accepted.length === 0) return

    const next = [
      ...current,
      ...accepted.map(file => ({
        id: getNewUuid(),
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      })),
    ]
    attachmentsRef.current = next
    setAttachments(next)
  }, [])

  const removeAttachment = useCallback((id: string) => {
    const current = attachmentsRef.current
    const target = current.find(a => a.id === id)
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)

    const next = current.filter(a => a.id !== id)
    attachmentsRef.current = next
    setAttachments(next)
    setError(null)
  }, [])

  const clearAttachments = useCallback(() => {
    attachmentsRef.current.forEach(a => a.previewUrl && URL.revokeObjectURL(a.previewUrl))
    attachmentsRef.current = []
    setAttachments([])
    setError(null)
  }, [])

  return { attachments, error, addFiles, removeAttachment, clearAttachments }
}
